import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { testimonials } from "@/mocks/data";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [activeTab, setActiveTab] = useState<"intro" | "reputation" | "contact">("intro");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== "") {
      setFormStatus("success");
      setFormSuccess("您的留言已提交成功，我们会尽快与您联系！");
      form.reset();
      return;
    }

    setFormStatus("loading");
    setFormError("");
    setFormSuccess("");

    try {
      const formData = new FormData(form);
      const res = await fetch("https://readdy.ai/api/form/d9pn1apn2trhb35gvi80", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (res.ok && parsed?.code === "OK") {
        setFormStatus("success");
        setFormSuccess("感谢您的留言！我们已收到您的信息，客服团队会在24小时内与您联系。");
        form.reset();
      } else {
        throw new Error(parsed?.meta?.message || parsed?.message || "提交失败，请稍后重试");
      }
    } catch (err: any) {
      setFormStatus("error");
      setFormError(err?.message || "网络异常，请稍后重试");
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "关于爱情岛网站",
            description: "了解爱情岛网站的品牌故事、核心价值、用户口碑及联系方式。爱情岛是在线交友与娱乐平台，为500万+用户提供优质服务。",
            url: "https://www.aiqingdao.com/about",
            isPartOf: {
              "@type": "WebSite",
              name: "爱情岛网站",
              url: "https://www.aiqingdao.com/",
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首页",
                item: "https://www.aiqingdao.com/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "关于我们",
              },
            ],
          }),
        }}
      />
      <Navbar />
      <main className="pt-16">
        <section className="py-12 md:py-16 bg-background-100">
          <div className="container-page">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">
                首页
              </Link>
              <span>/</span>
              <span className="text-foreground-900">关于我们</span>
            </nav>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              关于<strong>爱情岛网站</strong>
            </h1>
            <p className="text-foreground-600 max-w-2xl leading-relaxed">
              了解我们的品牌故事、核心价值观与联系方式。用心连接每一份美好缘分。
            </p>
          </div>
        </section>

        <section className="py-10 bg-background-50">
          <div className="container-page">
            <div className="flex flex-wrap gap-2 mb-10">
              {([
                { key: "intro", label: "品牌介绍" },
                { key: "reputation", label: "用户口碑" },
                { key: "contact", label: "联系我们" },
              ] as const).map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`whitespace-nowrap px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                    activeTab === tab.key
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === "intro" && (
              <div className="max-w-3xl">
                <div className="mb-12">
                  <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-6">品牌故事</h2>
                  <p className="text-foreground-700 leading-relaxed mb-5">
                    爱情岛网站创立于2018年，是深受用户喜爱的在线交友与娱乐平台。我们致力于通过技术创新和人性化设计，
                    为每一位用户打造安全、便捷、高效的交友环境。经过多年的发展，平台已积累了超过<strong>500万注册用户</strong>，
                    日均活跃用户超过30万，匹配成功率高达85%，成为中文在线交友领域的领先品牌。
                  </p>
                  <p className="text-foreground-700 leading-relaxed mb-5">
                    我们深知，每一次相遇都可能是一段美好缘分的开始。因此，爱情岛网站始终坚持以用户体验为核心，
                    不断优化智能匹配算法、丰富互动功能，并提供完善的安全保护机制，让用户能够放心交友、真诚互动。
                  </p>
                </div>

                <div className="mb-12">
                  <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-6">核心价值</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {[
                      { icon: "ri-heart-pulse-line", title: "真诚连接", desc: "致力于帮助用户找到真正的缘分，而非浮于表面的社交。" },
                      { icon: "ri-shield-check-line", title: "安全至上", desc: "多层安全防护体系，确保每一次交流都在安全的环境中进行。" },
                      { icon: "ri-lightbulb-line", title: "技术创新", desc: "持续投入研发，运用AI技术优化匹配和用户体验。" },
                      { icon: "ri-user-heart-line", title: "用户第一", desc: "以用户满意度为核心指标，不断优化产品和服务。" },
                    ].map((val, idx) => (
                      <div key={idx} className="flex gap-4 p-5 rounded-lg bg-background-100">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                          <i className={`${val.icon} text-lg`}></i>
                        </div>
                        <div>
                          <h3 className="text-base font-heading font-semibold text-foreground-900 mb-1.5">
                            {val.title}
                          </h3>
                          <p className="text-sm text-foreground-600 leading-relaxed">{val.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-6">发展历程</h2>
                  <div className="space-y-6">
                    {[
                      { year: "2018", event: "爱情岛网站正式上线，开启在线交友新篇章" },
                      { year: "2019", event: "注册用户突破100万，推出智能匹配系统1.0" },
                      { year: "2020", event: "上线App客户端，实现全平台覆盖" },
                      { year: "2021", event: "推出实名认证体系，用户安全大幅提升" },
                      { year: "2022", event: "注册用户突破300万，匹配算法升级至2.0" },
                      { year: "2023", event: "视频聊天功能上线，互动体验全面升级" },
                      { year: "2024", event: "用户突破500万，荣获年度最佳交友平台奖" },
                      { year: "2025", event: "AI智能匹配3.0发布，匹配成功率提升至85%" },
                      { year: "2026", event: "夏季大版本更新，UI全面焕新，功能持续丰富" },
                    ].map((item, idx) => (
                      <div key={idx} className="flex gap-5 items-start">
                        <div className="w-16 flex-shrink-0 text-right">
                          <span className="text-sm font-bold text-primary-500">{item.year}</span>
                        </div>
                        <div className="flex-1 pb-6 border-l-2 border-primary-200 pl-5">
                          <p className="text-sm text-foreground-700 leading-relaxed">{item.event}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "reputation" && (
              <div className="max-w-3xl">
                <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-6">用户口碑</h2>
                <div className="space-y-5">
                  {testimonials.map((test) => (
                    <div
                      key={test.id}
                      className="p-6 rounded-lg bg-background-100 flex gap-5"
                    >
                      <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0">
                        <img
                          src={test.avatar}
                          alt={test.name}
                          className="w-full h-full object-cover"
                          title={`${test.name} - 爱情岛用户评价`}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-sm font-semibold text-foreground-900">{test.name}</span>
                          <span className="text-xs text-foreground-500">
                            {test.age}岁 · {test.city}
                          </span>
                        </div>
                        <div className="flex gap-0.5 mb-3 text-accent-400">
                          {[...Array(5)].map((_, i) => (
                            <div key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                              <i className="ri-star-fill text-xs"></i>
                            </div>
                          ))}
                        </div>
                        <p className="text-sm text-foreground-700 leading-relaxed">"{test.content}"</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "contact" && (
              <div className="max-w-3xl">
                <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-6">联系我们</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                  {[
                    { icon: "ri-mail-line", title: "电子邮箱", info: "support@aiqingdao.com", desc: "24小时内回复" },
                    { icon: "ri-customer-service-2-line", title: "在线客服", info: "平台内右下角客服图标", desc: "9:00-22:00 在线" },
                    { icon: "ri-wechat-line", title: "微信公众号", info: "搜索「爱情岛」", desc: "获取最新动态" },
                  ].map((item, idx) => (
                    <div key={idx} className="p-5 rounded-lg bg-background-100 text-center">
                      <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                        <i className={`${item.icon} text-xl`}></i>
                      </div>
                      <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1.5">
                        {item.title}
                      </h3>
                      <p className="text-sm text-foreground-700 mb-1">{item.info}</p>
                      <p className="text-xs text-foreground-500">{item.desc}</p>
                    </div>
                  ))}
                </div>

                <h3 className="text-xl font-heading font-semibold text-foreground-900 mb-6">在线留言</h3>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-5 p-6 rounded-lg bg-background-100"
                >
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="sp-honeypot"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-2">
                        您的姓名 *
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-2">
                        电子邮箱 *
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-2">
                      留言主题 *
                    </label>
                    <input
                      type="text"
                      id="contact-subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入留言主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-2">
                      留言内容 *
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    ></textarea>
                  </div>

                  {formStatus === "success" && (
                    <div className="p-4 rounded-lg bg-secondary-100 text-secondary-700 text-sm">
                      <i className="ri-checkbox-circle-line mr-2"></i>
                      {formSuccess}
                    </div>
                  )}
                  {formStatus === "error" && (
                    <div className="p-4 rounded-lg bg-red-50 text-red-600 text-sm">
                      <i className="ri-error-warning-line mr-2"></i>
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === "loading"}
                    className="whitespace-nowrap px-8 py-3 rounded-full text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer"
                  >
                    {formStatus === "loading" ? (
                      <span className="inline-flex items-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        提交中...
                      </span>
                    ) : (
                      "提交留言"
                    )}
                  </button>
                </form>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}